import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { catchError, distinctUntilChanged, of, Subject, switchMap, takeUntil, tap, finalize, map, Observable } from 'rxjs';
import { helper } from 'src/environments/helper';
import { BorderouriAutomateService } from './borderouri-automate.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { PermissionsService } from 'src/app/services/permissions.service';
import { AuthService } from 'src/app/services/auth.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { BorderouAutomat, BorderouAutomatFilters, BorderouAutomatStats } from './borderouri-automate.interface';
import { GhiseuModuleService } from '../ghiseul-unic-settings/ghiseu-module/ghiseu-module.service';
import { LocatiiService } from '../ghiseul-unic-settings/locatii/locatii.service';
import { ServiciiService } from '../ghiseul-unic-settings/servicii/servicii.service';
import { FormControl, FormGroup } from '@angular/forms';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-borderouri-automate',
    templateUrl: './borderouri-automate.component.html',
    styleUrls: ['./borderouri-automate.component.scss']
})
export class BorderouriAutomateComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    isExporting = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    private destroy$ = new Subject<void>();

    // Stats
    stats: BorderouAutomatStats | null = null;

    // Liste pentru dropdown-uri
    module: any[] = [];
    locatii: any[] = [];
    servicii: any[] = [];

    // Liste filtrate
    filteredModule: any[] = [];
    filteredLocatii: any[] = [];
    filteredServicii: any[] = [];

    // FormControls pentru căutare în select
    moduleControl = new FormControl();
    locatiiControl = new FormControl();
    serviciiControl = new FormControl();

    // Form pentru filtre
    searchParamsForm!: FormGroup;

    // Valori inițiale form
    initialFormValues = {
        id_modul: -1,
        id_locatie: -1,
        id_serviciu: -1,
        target_date_from: '',
        target_date_to: '',
        locked: '-1',
        borderou_generated: '-1',
        emails_sent: '-1'
    };

    // URL pentru listare
    dtLink = environment.interop.basePath + 'api/programari/nomenclatoare/borderou-automat/list-dt';

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private borderoService: BorderouriAutomateService,
        public permissionsService: PermissionsService,
        private authService: AuthService,
        private ghiseuModuleService: GhiseuModuleService,
        private locatiiService: LocatiiService,
        private serviciiService: ServiciiService
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.buildSearchParamsForm();
        this.loadModule();
        this.loadLocatii();
        this.loadServicii();
        this.loadStats();
        this.renderFieldsSearch();
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.borderoService.setToken(token);
        });
    }

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            id_modul: new FormControl(-1),
            id_locatie: new FormControl(-1),
            id_serviciu: new FormControl(-1),
            target_date_from: new FormControl(''),
            target_date_to: new FormControl(''),
            locked: new FormControl('-1'),
            borderou_generated: new FormControl('-1'),
            emails_sent: new FormControl('-1')
        });
    }

    loadStats() {
        this.borderoService.getStats().subscribe({
            next: (res: any) => {
                this.stats = res.data;
            },
            error: (err) => {
                console.error('Error loading stats:', err);
            }
        });
    }

    renderFieldsSearch() {
        // Filter pentru module
        this.moduleControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredModule = this.module;
                    return of(this.module);
                }
                return this.filterModule(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredModule = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Filter pentru locații
        this.locatiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredLocatii = this.locatii;
                    return of(this.locatii);
                }
                return this.filterLocatii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredLocatii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Filter pentru servicii
        this.serviciiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredServicii = this.servicii;
                    return of(this.servicii);
                }
                return this.filterServicii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredServicii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterModule(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.module.filter(m =>
            this.normalizeString(m.name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterLocatii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.locatii.filter(l =>
            this.normalizeString(l.name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterServicii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.servicii.filter(s =>
            this.normalizeString(s.web_name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    onModuleSelectClosed() {
        this.moduleControl.setValue(null);
        this.filteredModule = this.module;
    }

    onLocatiiSelectClosed() {
        this.locatiiControl.setValue(null);
        this.filteredLocatii = this.locatii;
    }

    onServiciiSelectClosed() {
        this.serviciiControl.setValue(null);
        this.filteredServicii = this.servicii;
    }

    loadModule() {
        this.isFilterLoading = true;
        this.ghiseuModuleService.getModule().pipe(
            map((results: any) => [...results.data]),
            finalize(() => (this.isFilterLoading = false))
        ).subscribe({
            next: (res) => {
                this.module = res;
                this.filteredModule = res;
            },
            error: (err) => {
                console.error('Error in get ghiseu module: ', err);
            }
        });
    }

    loadLocatii() {
        this.isFilterLoading = true;
        this.locatiiService.getLocatii().pipe(
            finalize(() => (this.isFilterLoading = false))
        ).subscribe({
            next: (res) => {
                this.locatii = res;
                this.filteredLocatii = res;
            },
            error: (err) => {
                console.error('Error in get locatii: ', err);
            }
        });
    }

    loadServicii() {
        this.isFilterLoading = true;
        this.serviciiService.getServicii().pipe(
            map((results: any) => [...results.data]),
            finalize(() => (this.isFilterLoading = false))
        ).subscribe({
            next: (res) => {
                this.servicii = res;
                this.filteredServicii = res;
            },
            error: (err) => {
                console.error('Error in get servicii: ', err);
            }
        });
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    applyFilters(): void {
        this.rerenderDT(true);
        this.showCard = false;
    }

    resetForm(): void {
        this.searchParamsForm.reset(this.initialFormValues);

        this.moduleControl.setValue(null);
        this.locatiiControl.setValue(null);
        this.serviciiControl.setValue(null);

        this.filteredModule = this.module;
        this.filteredLocatii = this.locatii;
        this.filteredServicii = this.servicii;

        this.rerenderDT(true);
        this.showCard = true;
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            pagingType: 'full_numbers',
            responsive: true,
            pageLength: 25,
            searching: false,
            lengthMenu: [10, 25, 50, 100],
            serverSide: true,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                const params = {
                    ...dataTablesParameters,
                    filters: this.searchParamsForm.value
                };

                this.http
                    .post<DataTablesResponse>(this.dtLink, params)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                    width: '50px'
                },
                {
                    title: 'Data Programări',
                    data: 'target_date_formatted',
                    render: (data: string) => {
                        return data ? `<strong>${data}</strong>` : '-';
                    }
                },
                {
                    title: 'Modul',
                    data: 'denumire_modul',
                },
                {
                    title: 'Locație',
                    data: 'denumire_locatie',
                    render: (data: string) => data || '<span class="text-muted">-</span>'
                },
                {
                    title: 'Serviciu',
                    data: 'denumire_serviciu',
                },
                {
                    title: 'Tip Programare',
                    data: 'denumire_program_type',
                    render: (data: string) => data || '<span class="text-muted">-</span>'
                },
                {
                    title: 'Nr. Prog.',
                    data: 'programari_count',
                    width: '70px',
                    render: (data: number) => {
                        if (data === 0) return '<span class="badge bg-secondary">0</span>';
                        return `<span class="badge bg-primary">${data}</span>`;
                    }
                },
                {
                    title: 'Blocat',
                    data: 'locked',
                    width: '70px',
                    render: (data: boolean, type: any, row: any) => {
                        if (data) {
                            return `<span class="badge bg-danger" title="${row.locked_at_formatted || ''}">Da</span>`;
                        }
                        return '<span class="badge bg-success">Nu</span>';
                    }
                },
                {
                    title: 'Borderou',
                    data: 'borderou_generated',
                    width: '80px',
                    render: (data: boolean, type: any, row: any) => {
                        if (data) {
                            return `<span class="badge bg-info" title="${row.borderou_generated_at_formatted || ''}">Generat</span>`;
                        }
                        return '<span class="badge bg-warning text-dark">Nu</span>';
                    }
                },
                {
                    title: 'Email',
                    data: 'emails_sent',
                    width: '70px',
                    render: (data: boolean, type: any, row: any) => {
                        if (data) {
                            return `<span class="badge bg-success" title="${row.emails_sent_at_formatted || ''}">Trimis</span>`;
                        }
                        return '<span class="badge bg-secondary">Nu</span>';
                    }
                },
                {
                    title: 'Destinatari',
                    data: 'emails',
                    render: (data: string) => {
                        if (!data) return '<span class="text-muted">-</span>';
                        // Afișează email-urile pe linii separate dacă sunt mai multe
                        const emails = data.split(',').map((e: string) => e.trim());
                        if (emails.length === 1) {
                            return `<small>${emails[0]}</small>`;
                        }
                        return `<small title="${data}">${emails[0]}<br/>+${emails.length - 1} altele</small>`;
                    }
                },
                {
                    title: 'Creat la',
                    data: 'created_at_formatted',
                    width: '120px'
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    width: '140px',
                    render: (data: any, type: any, row: any) => {
                        let buttons = `<div class="d-flex align-left justify-content-left flex-wrap gap-1">`;

                        if (row.borderou_generated) {
                            buttons += `
                                <button type="button" class="action-btn view" title="Vizualizare HTML">
                                    <img class="btn-img" src="/assets/icons/view.svg" alt="view"/>
                                </button>
                                <button type="button" class="action-btn download" style="font-size: 11px; color: #fff" title="Descarcă PDF">
                                    <i class="btn-img fa fa-download"></i>
                                </button>
                                <button type="button" class="action-btn download-xlsx" style="font-size: 11px; color: #fff; background-color: #28a745" title="Descarcă XLSX">
                                    <i class="btn-img fa fa-file-excel"></i>
                                </button>
                                <button type="button" class="action-btn resend" style="font-size: 11px; color: #fff" title="Retrimite email">
                                    <i class="btn-img fa-solid fa-rotate-right"></i>
                                </button>
                            `;
                        }

                        buttons += `</div>`;
                        return buttons;
                    },
                },
            ],
            rowCallback: (row: Node, data: BorderouAutomat) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewIcon = lastTd?.querySelector('.view') as HTMLElement;
                const downloadIcon = lastTd?.querySelector('.download') as HTMLElement;
                const resendIcon = lastTd?.querySelector('.resend') as HTMLElement;

                if (viewIcon) {
                    if (!this.permissionsService.hasPermission('auth-module.ghiseu.borderou-automat.view')) {
                        viewIcon.style.display = 'none';
                    }
                    viewIcon.addEventListener('click', () => {
                        this.viewBorderou(data);
                    });
                }

                if (downloadIcon) {
                    if (!this.permissionsService.hasPermission('auth-module.ghiseu.borderou-automat.view')) {
                        downloadIcon.style.display = 'none';
                    }
                    downloadIcon.addEventListener('click', () => {
                        this.downloadPdf(data);
                    });
                }

                const xlsxIcon = lastTd?.querySelector('.download-xlsx') as HTMLElement;
                if (xlsxIcon) {
                    if (!this.permissionsService.hasPermission('auth-module.ghiseu.borderou-automat.view')) {
                        xlsxIcon.style.display = 'none';
                    }
                    xlsxIcon.addEventListener('click', () => {
                        this.downloadXlsx(data);
                    });
                }

                if (resendIcon) {
                    if (!this.permissionsService.hasPermission('auth-module.ghiseu.borderou-automat.resend')) {
                        resendIcon.style.display = 'none';
                    }
                    resendIcon.addEventListener('click', () => {
                        this.resendEmail(data);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        if (this.dtElement?.dtInstance) {
            this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
                dtInstance.draw(resetPaging);
            });
        }
    }

    viewBorderou(data: BorderouAutomat) {
        this.isLoading = true;
        this.borderoService.getHtml(data.id).subscribe({
            next: (html: string) => {
                this.isLoading = false;
                // Deschide într-o fereastră nouă
                const newWindow = window.open('', '_blank');
                if (newWindow) {
                    newWindow.document.write(html);
                    newWindow.document.close();
                }
            },
            error: (err) => {
                this.isLoading = false;
                let msg;
                try {
                    msg = err.error?.errors?.message?.[0] || 'Borderoul nu a fost încă generat.';
                } catch {
                    msg = 'Borderoul nu a fost încă generat.';
                }
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'warning',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    downloadPdf(data: BorderouAutomat) {
        this.isLoading = true;
        this.borderoService.downloadPdf(data.id).subscribe({
            next: (blob: Blob) => {
                this.isLoading = false;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `borderou_${data.target_date_formatted?.replace(/\./g, '-') || data.id}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.isLoading = false;
                let msg;
                try {
                    msg = err.error?.errors?.message?.[0] || 'PDF-ul nu a putut fi generat.';
                } catch {
                    msg = 'PDF-ul nu a putut fi generat.';
                }
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    downloadXlsx(data: BorderouAutomat) {
        this.isLoading = true;
        this.borderoService.downloadXlsx(data.id).subscribe({
            next: (blob: Blob) => {
                this.isLoading = false;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `borderou_${data.target_date_formatted?.replace(/\./g, '-') || data.id}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.isLoading = false;
                let msg;
                try {
                    msg = err.error?.errors?.message?.[0] || 'XLSX-ul nu a putut fi generat.';
                } catch {
                    msg = 'XLSX-ul nu a putut fi generat.';
                }
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    resendEmail(data: BorderouAutomat) {
        if (!data.emails) {
            Swal.fire({
                title: helper.dialogConfig.headers.errorAtentie,
                text: 'Nu există email-uri configurate pentru acest borderou.',
                icon: 'warning',
                confirmButtonText: helper.dialogConfig.buttonLabels.close,
            });
            return;
        }

        Swal.fire({
            title: 'Confirmare retrimitere',
            html: `Sunteți sigur că doriți să retrimiteți borderoul către:<br><strong>${data.emails}</strong>?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Da, retrimite',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed) {
                this.isLoading = true;
                this.borderoService.resend(data.id).subscribe({
                    next: (res: any) => {
                        this.isLoading = false;
                        Swal.fire({
                            title: 'Succes',
                            text: res.message || 'Email-ul a fost retrimis cu succes.',
                            icon: 'success',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        });
                        this.rerenderDT(false);
                    },
                    error: (err) => {
                        this.isLoading = false;
                        let msg;
                        try {
                            msg = err.error?.errors?.message?.[0] || 'Eroare la retrimiterea email-ului.';
                        } catch {
                            msg = 'Eroare la retrimiterea email-ului.';
                        }
                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: msg,
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        });
                    }
                });
            }
        });
    }

    exportExcel() {
        if (!this.permissionsService.hasPermission('auth-module.ghiseu.borderou-automat.export')) {
            Swal.fire({
                title: helper.dialogConfig.headers.errorAtentie,
                text: 'Nu aveți permisiunea de a exporta.',
                icon: 'warning',
                confirmButtonText: helper.dialogConfig.buttonLabels.close,
            });
            return;
        }

        this.isExporting = true;
        const filters: BorderouAutomatFilters = this.searchParamsForm.value;

        this.borderoService.export(filters).subscribe({
            next: (blob: Blob) => {
                this.isExporting = false;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `borderouri_automate_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.isExporting = false;
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: 'Eroare la export. Vă rugăm reîncercați.',
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }
}
